import * as React from 'react'
import * as z from 'zod'
import { useQuery } from '@tanstack/react-query'

import { Repository, repositorySchema } from '@basehub/data/replicache/index'
import { useUserStore } from '~/app/_store/user-store'
import { useUserTeamsList } from './team/use-user-teams-list'
import { invariant } from '@epic-web/invariant'
import { isPlaygroundUser } from '@basehub/data/replicache/playground/ids'

export const usePersonalTeamAndRepos = () => {
  const {
    data: { teams: teamList },
  } = useUserTeamsList()
  const optimisticUser = useUserStore((s) => s.optimisticUser)

  const personalTeam = React.useMemo(
    () =>
      !optimisticUser
        ? undefined
        : teamList?.find((team) => {
            if (team.plan !== 'personal' && team.plan_v2?.type !== 'personal') {
              return false
            }

            return team.members.some(
              (member) =>
                member.userId === optimisticUser.id && member.role === 'owner'
            )
          }),
    [teamList, optimisticUser]
  )

  const { data } = useQuery(
    ['personal-team-repos', personalTeam?.repoIds ?? []],
    async (): Promise<Array<Repository>> => {
      invariant(!!optimisticUser, `Shouldn't be enabled`)
      const userIsPlayground = isPlaygroundUser(optimisticUser?.id)
      if (userIsPlayground) return []

      const repositories = await fetch(
        '/api/team/get-personal-team-repos'
      ).then((res) => res.json())
      const parsedRepos = z.array(repositorySchema).parse(repositories)
      return parsedRepos
    },
    { enabled: !!optimisticUser }
  )

  return { team: personalTeam, repositories: data }
}
